/**
 * Account menu (TMMAPP-0004, design §14.3; the prototype's user menu, TMMAPP-0100): the identity
 * block (a D1 extra), a "Language" row of locale chips, Theme and Density as compact segmented
 * rows (D1), then Profile / Billing (the Settings tabs) and Sign out after a `line` rule, each
 * with the prototype's glyph. "What's new" (TMMAPP-0142 U2 item 13a) opens the team's announcements
 * for everyone, a guest included, with the unread count as its badge.
 *
 * The preferences are real controls, so they sit in the popover above the `menu` list, never
 * inside it: a `menu` owns only menu items, and a control nested in one is not reachable.
 */
import { Box, Divider, MenuItem, MenuList, Popover, Typography, useTheme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/authHooks';
import { DensitySelect } from '@/components/DensitySelect';
import { DeployEnvBadge } from '@/components/DeployEnvBadge';
import { Pill } from '@/components/Pill';
import { useAnnouncements } from '@/features/announcements/announcementsContext';
import { formatCivilDate, formatNumber } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { LanguageChips } from '@/components/LanguageChips';
import { ThemeModeSelect } from '@/components/ThemeModeSelect';
import { settingsHref } from '@/features/settings/settingsHref';
import { useMe } from '@/session/meHooks';
import { resolveTokens } from '@/theme/theme';
import { roleLabelKey } from './roleKey';

const PROFILE_PATH = settingsHref('profile');
const BILLING_PATH = settingsHref('subscription');

interface AccountMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

/**
 * The prototype's glyph before a user-menu item (visual contract §2: ○ Profile, ▤ Billing, → Sign
 * out), in an 18px `text3` slot. Decorative: the item's text is its name.
 */
function Glyph({ children }: { children?: string }) {
  const tokens = resolveTokens(useTheme());
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        width: 18,
        flexShrink: 0,
        textAlign: 'center',
        fontSize: 15,
        lineHeight: '18px',
        color: tokens.text3,
      }}
    >
      {children}
    </Box>
  );
}

const ITEM_SX = { gap: 1.5, p: '10px 16px', fontSize: 14 } as const;

/** A section of the menu's head: padding 8 16 12, a `line` rule under it (the prototype's language block). */
function Section({ label, children }: { label?: string; children: React.ReactNode }) {
  const tokens = resolveTokens(useTheme());
  return (
    <Box sx={{ p: '8px 16px 12px', borderBottom: '1px solid', borderColor: tokens.line }}>
      {label !== undefined && (
        <Typography
          variant="overline"
          component="div"
          sx={{ color: tokens.textLabel('surfaceRaised'), mb: '7px' }}
        >
          {label}
        </Typography>
      )}
      {children}
    </Box>
  );
}

export function AccountMenu({ anchorEl, onClose }: AccountMenuProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();
  const me = useMe();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const locale = useLocale();
  const announcements = useAnnouncements();
  const unread = announcements?.unreadCount ?? 0;

  function go(path: string) {
    onClose();
    void navigate(path);
  }

  return (
    <Popover
      anchorEl={anchorEl}
      open={anchorEl !== null}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          // The popover is a named dialog holding the preferences and the menu (the trigger's
          // `aria-haspopup="dialog"`), so focus never lands on an unnamed panel.
          role: 'dialog',
          'aria-label': t('shell.accountMenu'),
          sx: {
            minWidth: 196,
            py: '6px',
            boxShadow: tokens.elevation.dialog,
            borderRadius: `${tokens.radius.control}px`,
          },
        },
      }}
    >
      {/* Identity (a D1 extra): the v2 top bar shows only the avatar, so the name, the role line
          and the trial's end date (TMMAPP-0150 U2: they left the bar) are reachable here. */}
      <Section>
        <Typography variant="body2" component="div" sx={{ fontSize: 14, fontWeight: 500 }}>
          {me.user.displayName}
        </Typography>
        <Typography variant="caption" component="div" sx={{ fontSize: 12, color: tokens.text2 }}>
          {t(roleLabelKey(me.tier, me.trialEndsOn))}
        </Typography>
        {me.trialEndsOn !== null && (
          <Typography variant="caption" component="div" sx={{ fontSize: 12, color: tokens.text2 }}>
            {t('shell.trialUntil', { date: formatCivilDate(me.trialEndsOn, locale) })}
          </Typography>
        )}
        {/* The staging badge leaves the top bar at `xs` and shows here (TMMAPP-0154 §A 4). */}
        <Box data-menu-env="" sx={{ display: { xs: 'block', sm: 'none' }, mt: '6px' }}>
          <DeployEnvBadge />
        </Box>
      </Section>
      <Section label={t('language.label')}>
        <LanguageChips />
      </Section>
      <Section label={t('theme.label')}>
        <ThemeModeSelect fullWidth />
      </Section>
      <Section label={t('density.label')}>
        <DensitySelect fullWidth />
      </Section>
      <MenuList aria-label={t('shell.accountMenu')} sx={{ py: 0 }}>
        <MenuItem onClick={() => go(PROFILE_PATH)} sx={ITEM_SX}>
          <Glyph>○</Glyph>
          {t('shell.profile')}
        </MenuItem>
        {me.tier === 'GUEST' && (
          <MenuItem
            onClick={() => {
              onClose();
              void navigate('/me/own-books', { state: { from: location.pathname } });
            }}
            sx={ITEM_SX}
          >
            <Glyph />
            {t('guest.ownBooks')}
          </MenuItem>
        )}
        {announcements !== null && (
          <MenuItem
            onClick={() => {
              onClose();
              announcements.openWhatsNew();
            }}
            sx={ITEM_SX}
            data-whats-new-item=""
          >
            <Glyph>✦</Glyph>
            <Box component="span" sx={{ flex: 1 }}>
              {t('support.announcements.whatsNew')}
            </Box>
            {unread > 0 && (
              <Box component="span" data-unread={unread}>
                <Pill wash="brand">
                  <span aria-hidden>{formatNumber(unread, locale)}</span>
                  <Box component="span" sx={visuallyHidden}>
                    {t('support.announcements.unreadCount', { count: unread })}
                  </Box>
                </Pill>
              </Box>
            )}
          </MenuItem>
        )}
        {(me.tier === 'SUBSCRIBER' || me.locked) && (
          <MenuItem onClick={() => go(BILLING_PATH)} sx={ITEM_SX}>
            <Glyph>▤</Glyph>
            {t('shell.billing')}
          </MenuItem>
        )}
        <Divider sx={{ my: '6px' }} />
        <MenuItem
          onClick={() => {
            onClose();
            void signOut();
          }}
          sx={ITEM_SX}
        >
          <Glyph>→</Glyph>
          {t('auth.signOut')}
        </MenuItem>
      </MenuList>
    </Popover>
  );
}
